import { z } from 'zod';
import { IsoDateStringSchema, UuidV7Schema } from './common.js';
import { DeliverySystemSchema } from './verticale.js';

/**
 * Lead delivery domain — runtime config (AppSetting key `delivery`),
 * lifecycle status enum, and read-shape DTOs for the admin endpoints.
 */

export const DeliveryStatusSchema = z.enum([
  'pending',
  'in_flight',
  'delivered',
  'failed',
  'skipped_test',
  'skipped_no_partner',
  /** Lead créé hors-ligne ; le vendeur n'a pas saisi le code OTP dans
   *  les 1h imparties (ou 30 min côté SMS). Pas de retransmission. */
  'skipped_no_consent',
]);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

/** Per-verticale Collect mapping — points to a LP + the step we land on. */
export const CollectLpRefSchema = z.object({
  pageId: z.string().uuid(),
  stepId: z.string().min(1).default('final-step'),
});
export type CollectLpRef = z.infer<typeof CollectLpRefSchema>;

/**
 * AppSetting('delivery') — admin-editable runtime config.
 *
 * Doorly always ships leads through Hipto Collect (`POST /collect/validate`).
 * Collect itself routes the lead downstream to Hopti based on the
 * landing-page flow configuration on the Collect side (the historical
 * DALI → Manager path was retired in 2026-08). Doorly only needs the LP id
 * for each verticale.
 *
 * `LeadDelivery.deliverySystem` (manager/hopti) is therefore *informational
 * only* in Doorly — it records where the lead ended up (manager on
 * pre-2026-08 historical rows), but doesn't change which API we call.
 */
export const DeliveryConfigSchema = z.object({
  /**
   * When true, the provider DOES log an `HttpAuditLog` row representing the
   * call it would have made, but never hits the network. Default true so the
   * MVP rollout doesn't accidentally ship leads while we wire up the front.
   */
  stub: z.boolean().default(true),
  collectBaseUrl: z.string().url().or(z.literal('')).default(''),
  /** Map verticale.slug → LP for the Collect endpoint. */
  lpByVerticaleSlug: z.record(z.string(), CollectLpRefSchema).default({}),
});
export type DeliveryConfig = z.infer<typeof DeliveryConfigSchema>;

export const DEFAULT_DELIVERY_CONFIG: DeliveryConfig = {
  stub: true,
  collectBaseUrl: '',
  lpByVerticaleSlug: {
    telco: { pageId: 'b9abb595-992b-4d74-8ecf-e33b84c582e1', stepId: 'final-step' },
    energie: { pageId: 'db7281a1-d87b-4fab-bb08-e90d427935d0', stepId: 'final-step' },
  },
};

// ── Read-shape DTOs ─────────────────────────────────────

export const HttpAuditLogItemSchema = z.object({
  id: UuidV7Schema,
  deliveryId: UuidV7Schema.nullable(),
  direction: z.string(),
  provider: z.string(),
  providerOperation: z.string().nullable(),
  httpMethod: z.string(),
  url: z.string(),
  requestHeaders: z.unknown().nullable(),
  requestBody: z.unknown().nullable(),
  requestBodyTruncated: z.boolean(),
  responseStatus: z.number().int().nullable(),
  responseHeaders: z.unknown().nullable(),
  responseBody: z.unknown().nullable(),
  responseBodyTruncated: z.boolean(),
  durationMs: z.number().int().nullable(),
  isError: z.boolean(),
  errorType: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: IsoDateStringSchema,
});
export type HttpAuditLogItem = z.infer<typeof HttpAuditLogItemSchema>;

export const LeadDeliveryItemSchema = z.object({
  id: UuidV7Schema,
  leadId: UuidV7Schema,
  partnerId: UuidV7Schema.nullable(),
  /** Effective destination after Collect routing — null until delivered. */
  deliverySystem: DeliverySystemSchema.nullable(),
  status: DeliveryStatusSchema,
  attempts: z.number().int(),
  externalId: z.string().nullable(),
  customerId: z.string().nullable(),
  lastError: z.string().nullable(),
  /** Firebase document id returned for TEMPORISATION leads (used for reschedule). */
  temporisationDocumentId: z.string().nullable(),
  /** Slot the lead is queued for — null if direct-send. */
  scheduledFor: IsoDateStringSchema.nullable(),
  startedAt: IsoDateStringSchema.nullable(),
  completedAt: IsoDateStringSchema.nullable(),
  createdAt: IsoDateStringSchema,
  updatedAt: IsoDateStringSchema,
});
export type LeadDeliveryItem = z.infer<typeof LeadDeliveryItemSchema>;

export const LeadDeliveryWithAuditSchema = LeadDeliveryItemSchema.extend({
  auditLogs: z.array(HttpAuditLogItemSchema),
});
export type LeadDeliveryWithAudit = z.infer<typeof LeadDeliveryWithAuditSchema>;

/** Body of POST /delivery/:leadId/reschedule. */
export const RescheduleDeliverySchema = z.object({
  scheduledAt: IsoDateStringSchema,
});
export type RescheduleDeliveryInput = z.infer<typeof RescheduleDeliverySchema>;
